import type {
  AuthenticatedMe,
  ChangePasswordInput,
  ForgotPasswordInput,
  ContentAvailability,
  LoginInput,
  MeResponse,
  RegisterInput,
  RegistrationFormSettings,
  ResetPasswordInput,
  SessionUser,
  UpdateProfileInput,
  VerifyEmailInput,
} from '@creativitas/shared';
import { ApiError, apiFetch } from '@/lib/api';

/**
 * The session lives in an httpOnly cookie the browser sends automatically, so
 * there is no token for the client to store or leak (§64).
 */

/** Resolves to `null` for an anonymous visitor rather than throwing. */
export async function fetchSession(): Promise<AuthenticatedMe | null> {
  try {
    const response = await apiFetch<MeResponse>('/me');
    return response.user ? { user: response.user, access: response.access } : null;
  } catch (error) {
    // A stale cookie against a reset database still ends up here.
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export const login = (input: LoginInput): Promise<AuthenticatedMe> =>
  apiFetch<AuthenticatedMe>('/auth/login', { method: 'POST', body: input });

export type RegisterResponse = {
  user: SessionUser;
  /** Аккаунт заведён, но до подтверждения почты войти нельзя (§14). */
  awaitingEmailVerification: boolean;
  /** Ушло ли письмо. Не ушло — предлагаем отправить заново, а не молчим. */
  verificationMailSent: boolean;
};

export const register = (input: RegisterInput): Promise<RegisterResponse> =>
  apiFetch<RegisterResponse>('/auth/register', { method: 'POST', body: input });

export const logout = (): Promise<void> => apiFetch<void>('/auth/logout', { method: 'POST' });

export const fetchAvailability = (): Promise<{ items: ContentAvailability[] }> =>
  apiFetch<{ items: ContentAvailability[] }>('/me/availability');

export const updateProfile = (input: UpdateProfileInput): Promise<MeResponse> =>
  apiFetch<MeResponse>('/me', { method: 'PATCH', body: input });

/** The server re-issues the cookie, so the tab stays signed in. */
export const changePassword = (input: ChangePasswordInput): Promise<MeResponse> =>
  apiFetch<MeResponse>('/me/password', { method: 'POST', body: input });

/**
 * Что умеет эта установка платформы.
 *
 * Пока нужно ровно для одного: показывать «Забыли пароль?» только там, где
 * ссылка действительно приведёт к письму. Обещать письмо, которого не будет,
 * хуже, чем не предлагать восстановление вовсе.
 */
export const fetchCapabilities = (): Promise<{
  passwordReset: boolean;
  emailVerification: boolean;
}> => apiFetch<{ passwordReset: boolean; emailVerification: boolean }>('/auth/capabilities');

/** Ответ один и тот же для любого адреса — по нему ничего не узнать. */
export const requestPasswordReset = (input: ForgotPasswordInput): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/auth/forgot-password', { method: 'POST', body: input });

/**
 * Токен уходит телом запроса, а не в адресе: в ссылке он стоит после решётки,
 * браузер такой фрагмент серверу не отправляет, и в журналы он не попадает.
 */
export const resetPassword = (input: ResetPasswordInput): Promise<{ ok: true; email: string }> =>
  apiFetch<{ ok: true; email: string }>('/auth/reset-password', { method: 'POST', body: input });

/**
 * Подтверждение почты по ссылке из письма (§14).
 *
 * Токен уходит телом — по той же причине, что и при смене пароля: в ссылке он
 * стоит после решётки и в журналы веб-сервера не попадает.
 */
export const verifyEmail = (input: VerifyEmailInput): Promise<{ ok: true; email: string }> =>
  apiFetch<{ ok: true; email: string }>('/auth/verify-email', { method: 'POST', body: input });

/** Ответ один и тот же для любого адреса — по нему ничего не узнать. */
export const resendVerification = (input: ForgotPasswordInput): Promise<{ ok: true }> =>
  apiFetch<{ ok: true }>('/auth/resend-verification', { method: 'POST', body: input });

/** Надписи и согласия экрана регистрации: их правит владелец платформы (§14). */
export const fetchRegistrationForm = (): Promise<RegistrationFormSettings> =>
  apiFetch<RegistrationFormSettings>('/auth/registration-form');
